import { Flex, Kbd, Text, type TextSize } from '@/components/ui';
import type { FlexProps } from './Flex';

import React, { ElementType, forwardRef, ReactNode } from 'react';

import clsx from 'clsx';

export type ListItemProps<T extends ElementType = typeof Flex> = {
  as?: T;
  component?: T; // Material-UI compatibility
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  hotkey?: ReactNode;
  primary?: ReactNode;
  secondary?: ReactNode;
  children?: ReactNode;
  selected?: boolean;
  disabled?: boolean;
  className?: string;
  onClick?: React.MouseEventHandler;
  href?: string;
  inverse?: boolean;
  tabIndex?: number;
  disableHoverEffect?: boolean;
  size?: TextSize;
} & Omit<
  T extends typeof Flex
    ? FlexProps
    : React.ComponentPropsWithoutRef<T>,
  | 'as'
  | 'component'
  | 'children'
  | 'className'
  | 'onClick'
  | 'href'
  | 'tabIndex'
>;

/**
 * Universal ListItem component for use in lists, menus, navigation, etc.
 * By default uses Flex component and inherits all Flex props (ml, gap, align, etc.)
 */
function ListItemComponent<T extends ElementType = typeof Flex>(
  props: ListItemProps<T>,
  ref: React.ForwardedRef<unknown>
) {
  const {
    as: Component,
    component, // Material-UI fallback
    startIcon,
    endIcon,
    hotkey,
    primary,
    secondary,
    children,
    selected = false,
    disabled = false,
    className,
    onClick,
    href,
    tabIndex,
    inverse = false,
    size,
    disableHoverEffect = false,
    ...rest
  } = props;

  // Use component as fallback for as, then Flex as default
  const ActualComponent = Component || component || Flex;

  const listItemContent = (
    <>
      {startIcon && (
        <span className="list-item__icon list-item__icon--start">
          {startIcon}
        </span>
      )}
      <span className="list-item__content">
        {primary || secondary ? (
          <>
            {primary && (
              <Text as={'span'} size={size} className="list-item__primary">
                {primary}
              </Text>
            )}
            {secondary && (
              <Text as={'span'} size={size} className="list-item__secondary">
                {secondary}
              </Text>
            )}
          </>
        ) : (
          children
        )}
      </span>
      {hotkey && (
        <Kbd
          size="medium"
          hotkey={String(hotkey)}
          className="list-item__hotkey"
        />
      )}
      {endIcon && (
        <span className="list-item__icon list-item__icon--end">
          {endIcon}
        </span>
      )}
    </>
  );

  const listItemClassName = clsx(
    'list-item',
    {
      'list-item--selected active': selected,
      'list-item--disabled': disabled,
      'hover-effect': !disabled && !disableHoverEffect,
      inverse: inverse,
    },
    className // This will include classes from parent (e.g., DropdownMenuItem)
  );

  const listItemProps = {
    className: listItemClassName,
    onClick: disabled ? undefined : onClick,
    href: href || undefined,
    tabIndex: typeof tabIndex === 'number' ? tabIndex : disabled ? -1 : 0,
    'aria-disabled': disabled || undefined,
    ...rest,
  };

  return (
    <ActualComponent
      ref={ref as unknown as React.Ref<HTMLDivElement>}
      {...listItemProps}
    >
      {listItemContent}
    </ActualComponent>
  );
}

export const ListItem = forwardRef(ListItemComponent) as unknown as <
  T extends ElementType = typeof Flex,
>(
  props: ListItemProps<T> & { ref?: React.ForwardedRef<unknown> }
) => React.ReactElement;

(
  ListItem as unknown as {
    displayName?: string;
  }
).displayName = 'ListItem';

export default ListItem;
